import { useState, useEffect } from 'react'
import type { FormEvent } from 'react'
import { adminSignIn, adminResetPassword, adminGetEmail } from '../lib/supabaseAdmin'
import { ShieldLogo } from './ShieldLogo'

const SUPERADMIN_EMAIL = 'immigrantshield@gmail.com'

export function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const [authed, setAuthed]         = useState(false)
  const [checking, setChecking]     = useState(true)
  const [email, setEmail]           = useState('')
  const [password, setPassword]     = useState('')
  const [error, setError]           = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [resetSent, setResetSent]   = useState(false)
  const [resetting, setResetting]   = useState(false)

  useEffect(() => {
    const storedEmail = adminGetEmail()
    setAuthed(storedEmail === SUPERADMIN_EMAIL)
    setChecking(false)
  }, [])

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-page)' }}>
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-[var(--brand)]" />
      </div>
    )
  }

  if (authed) return children

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    const { error: signInError, email: signedEmail } = await adminSignIn(email, password)
    if (signInError) { setError(signInError); setSubmitting(false); return }
    if (signedEmail !== SUPERADMIN_EMAIL) { setError('Conta não autorizada.'); setSubmitting(false); return }
    setAuthed(true)
    setSubmitting(false)
  }

  async function handleForgotPassword() {
    if (resetting) return
    setResetting(true)
    setError('')
    const { error: resetError } = await adminResetPassword()
    if (resetError) setError(resetError)
    else setResetSent(true)
    setResetting(false)
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: 'var(--bg-page)' }}>
      <div className="w-full max-w-sm rounded-2xl p-8 flex flex-col gap-6"
        style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-card)' }}>
        <div className="flex flex-col items-center gap-3">
          <ShieldLogo size={36} />
          <div className="text-center">
            <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Admin</h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Acesso restrito</p>
          </div>
        </div>
        {resetSent ? (
          <div className="text-center flex flex-col gap-3">
            <p className="text-sm" style={{ color: 'var(--text-primary)' }}>Link de redefinição enviado para o e-mail cadastrado.</p>
            <button onClick={() => setResetSent(false)} className="text-sm underline" style={{ color: 'var(--brand)' }}>Voltar ao login</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <input type="email" placeholder="E-mail" value={email} onChange={e => setEmail(e.target.value)} required
              className="w-full rounded-lg px-4 py-2.5 text-sm outline-none"
              style={{ backgroundColor: 'var(--bg-input)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} />
            <input type="password" placeholder="Senha" value={password} onChange={e => setPassword(e.target.value)} required
              className="w-full rounded-lg px-4 py-2.5 text-sm outline-none"
              style={{ backgroundColor: 'var(--bg-input)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} />
            <div className="flex items-center justify-between">
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Sessão expira ao fechar a aba</span>
              <button type="button" onClick={handleForgotPassword} disabled={resetting}
                className="text-sm transition-opacity" style={{ color: 'var(--brand)', opacity: resetting ? 0.6 : 1 }}>
                {resetting ? 'Enviando...' : 'Esqueceu a senha?'}
              </button>
            </div>
            {error && <p className="text-sm text-center" style={{ color: '#EF4444' }}>{error}</p>}
            <button type="submit" disabled={submitting}
              className="w-full rounded-lg py-2.5 text-sm font-semibold transition-opacity"
              style={{ backgroundColor: 'var(--brand)', color: '#fff', opacity: submitting ? 0.6 : 1 }}>
              {submitting ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
